import { NAV_ITEMS } from './navigation'
import { LEGACY_ROUTES } from './redirects'

/**
 * The organization in the address: `/fleettest/board` rather than `/board`.
 *
 * Presentation, never authority. Which organization a request reads is decided by the signed tenant
 * claim in the token and nothing else (`TenantScopeMiddleware`), so editing this segment cannot show
 * anybody another organization's data - at most it sends them back to their own. What the segment
 * buys is a sign-in link that names the organization, a bookmark or a pasted link that says whose
 * console it belongs to, and an address bar an MSP technician juggling ten customers can read.
 *
 * It is the router's `basename`, fixed when the page loads, so none of the console's own links or
 * `navigate` calls know it exists. Moving between organizations is therefore a full page load rather
 * than a navigation: a router cannot change its basename in place. The session lives in local storage
 * and survives the load.
 *
 * The platform organization has no segment. Its identifier is `platform`, which is also the first
 * segment of its own screens, and there is nothing to be gained by `/platform/platform/tenants`.
 */

/**
 * One DNS label, lower case, two to sixty-three characters.
 *
 * Mirrored from `TenantSlugs` in `AFilter.Server.Identity`, which is the authority on what may be
 * created. An identifier from before that rule that fails this pattern is not an error here: the
 * console gives that organization unprefixed addresses, which is exactly what it did before.
 */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/

/** First segments that belong to the edge, the SPA container's files, or names held for later. */
const SERVER_RESERVED = [
  'api', 'health', 'healthz', 'assets', 'fonts', 'brand', 'static', 'well-known',
  'favicon', 'robots', 'sitemap', 'manifest',
  'admin', 'app', 'auth', 'console', 'docs', 'download', 'downloads', 'help', 'kit', 'kits',
  'login', 'logout', 'oauth', 'sign-out', 'sign-up', 'signin', 'signout', 'signup', 'sso',
  'status', 'support', 'www', 'coastline', 'afilter'
] as const

/**
 * Every console route that is not reached from the navigation, so the derived list below cannot
 * miss it. `App.tsx` routes each of these; `orgPath.test.ts` reads that file and fails if a route's
 * first segment is missing from `RESERVED_SEGMENTS`, so a new screen cannot quietly become somebody's
 * organization.
 */
const UNLISTED_ROUTES = ['sign-in', 'dictionaries', 'events', 'platform'] as const

function firstSegment(path: string): string {
  return path.split('?')[0]!.split('/').filter(Boolean)[0] ?? ''
}

/**
 * First path segments that are never an organization.
 *
 * Derived from the navigation and the legacy redirects rather than retyped, so adding a screen to
 * either reserves its name without anybody remembering to.
 */
export const RESERVED_SEGMENTS: ReadonlySet<string> = new Set<string>([
  ...SERVER_RESERVED,
  ...UNLISTED_ROUTES,
  ...NAV_ITEMS.map((item) => firstSegment(item.to)),
  ...LEGACY_ROUTES.map((route) => firstSegment(route.from))
])

/** `v1`, `v2` and every later version of the device wire, which the edge routes to the collector. */
function isVersionSegment(segment: string): boolean {
  return /^v[0-9]+$/.test(segment)
}

/** Whether a first path segment names an organization rather than a route or a file. */
export function isOrgSegment(segment: string): boolean {
  const lower = segment.toLowerCase()
  return SLUG_PATTERN.test(lower) && !RESERVED_SEGMENTS.has(lower) && !isVersionSegment(lower)
}

/**
 * The organization segment exactly as it appears in the address, or null when there is none.
 *
 * As typed rather than lower-cased, because deciding whether the address is already canonical means
 * comparing what is there - `/FleetTest/board` names the right organization and is still worth
 * rewriting to `/fleettest/board`.
 */
export function orgSegment(pathname: string): string | null {
  const segment = pathname.split('/')[1] ?? ''
  return isOrgSegment(segment) ? segment : null
}

/** The organization the address names, lower-cased, or null. */
export function orgFromPath(pathname: string): string | null {
  return orgSegment(pathname)?.toLowerCase() ?? null
}

/** The router basename for an organization: `/fleettest`, or empty for none. */
export function basenameFor(org: string | null): string {
  return org ? `/${org}` : ''
}

/** The address with its organization segment removed: what the router matches against. */
export function routePath(pathname: string): string {
  const segment = orgSegment(pathname)
  if (!segment) return pathname
  return pathname.slice(segment.length + 1) || '/'
}

export interface OrgIdentity {
  tenantSlug?: string | null
  roles: readonly string[]
}

/**
 * The organization segment a signed-in identity's addresses carry, or null for none.
 *
 * None for the platform organization, whose screens are already under `/platform`. Identified by role
 * rather than by slug for the same reason `homeRouteFor` is: a platform administrator is somebody
 * holding `platform-admin`, and an assumed session never carries it, so a support session inside a
 * customer organization gets that customer's segment. None, too, for an identifier the console cannot
 * put in a path - one created before `TenantSlugs` existed.
 */
export function orgFor(identity: OrgIdentity): string | null {
  if (identity.roles.includes('platform-admin')) return null
  const slug = identity.tenantSlug?.trim().toLowerCase() ?? ''
  return isOrgSegment(slug) ? slug : null
}

/**
 * Where a signed-in identity should be, when the address is not already it.
 *
 * Null when the address is canonical, which is the common case and the one that must not reload
 * anything. Otherwise the same route under the right organization: an unprefixed bookmark gains its
 * segment, a segment in the wrong case is rewritten, and somebody signed in to one organization who
 * opens another's link is taken to the same screen in their own - never shown the other's, which the
 * API would refuse anyway.
 */
export function canonicalAddress(identity: OrgIdentity, pathname: string, search = '', hash = ''): string | null {
  if (orgSegment(pathname) === orgFor(identity)) return null
  return basenameFor(orgFor(identity)) + routePath(pathname) + search + hash
}

/**
 * Whether the address is another organization's sign-in page.
 *
 * The one place a signed-in person is shown a sign-in form instead of being redirected: somebody at
 * an MSP holding accounts in several customers switches between them by opening the customer's
 * sign-in link, and sending them back to the organization they are leaving would make the link
 * useless to exactly the person it was written for.
 */
export function isOtherOrgSignIn(identity: OrgIdentity, pathname: string): boolean {
  const named = orgFromPath(pathname)
  return named !== null && named !== orgFor(identity) && routePath(pathname) === '/sign-in'
}

/**
 * Why this cannot be a new organization's ID, in a sentence, or null when it can.
 *
 * The same rule, in the same order and nearly the same words, as `TenantSlugs.Problem` on the
 * server, which remains the authority. Here so the form creating an organization can say what is
 * wrong while the ID is being typed rather than after a round trip - and so a platform administrator
 * is never shown a sign-in address that the organization would not in fact be given.
 */
export function slugProblem(slug: string): string | null {
  if (slug.length < 2 || slug.length > 63)
    return `An organization ID is 2 to 63 characters. This one is ${slug.length}.`
  if (!/^[a-z0-9-]+$/.test(slug))
    return 'Use only lower-case letters, digits and hyphens - no spaces, dots, underscores or accents.'
  if (slug.startsWith('-') || slug.endsWith('-')) return 'An organization ID does not begin or end with a hyphen.'
  if (RESERVED_SEGMENTS.has(slug) || isVersionSegment(slug))
    return `"${slug}" is reserved, because the console or the device wire already uses it as an address.`
  return null
}
